import { RESULT_RANGE } from "@loot-os/api-contract";

export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : value;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** A timestamp to the second and without its year, for a log whose rows share a minute. */
export function formatLogTime(value: Date | string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** A timestamp to the millisecond with its zone, for a log's detail where two rows share a second. */
export function formatPreciseDateTime(value: Date | string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
    timeZoneName: "short",
  });
}

/** How long before `now` a moment was: "3 minutes ago", hours within a day, days after. */
export function formatRelativeTime(iso: string, now: Date): string {
  const minutes = Math.round((now.getTime() - new Date(iso).getTime()) / 60_000);
  const rtf = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });
  if (minutes < 60) return rtf.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (hours < 24) return rtf.format(-hours, "hour");
  return rtf.format(-Math.round(hours / 24), "day");
}

/** Date without its time, for dense table columns; pair with `formatFullDateTime` on hover. */
export function formatShortDate(value: Date | string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** The whole timestamp — weekday, seconds and time zone — for a tooltip over a shortened date. */
export function formatFullDateTime(value: Date | string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return date.toLocaleString(undefined, { dateStyle: "full", timeStyle: "long" });
}

/**
 * Real money — Stripe charges, disputes, revenue, vendor prices — in USD. A shopper's balance
 * and a box price are cents too, but they read through the environment's currency
 * (`formatAmount` from api-contract via `useCurrency`): identical output for a credits tenant,
 * "1,000 coins" beside "$9.99" for a custom one.
 */
export function centsToDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Odds of an integer weight out of the roll range, e.g. "12.3456%".
 *
 * Deliberately not api-contract's `getItemChance`, which returns a number with
 * trailing zeros stripped: this feeds a monospace column in the drop-table
 * editor, where a ragged "10%" beside "0.0100%" is unreadable.
 */
export function formatOdds(weight: number): string {
  return `${((weight / RESULT_RANGE) * 100).toFixed(4)}%`;
}

export function dropTableEvClient(items: { weight: number; value: number }[]): number {
  return items.reduce((sum, item) => sum + (item.weight / RESULT_RANGE) * item.value, 0);
}

export function suggestedPrice(ev: number, winRate = 0.85): number {
  return Math.ceil(ev / winRate);
}

/** Table cells: an empty value reads as a dash rather than a blank. */
export function dashIfEmpty(value: string | number | null | undefined): string {
  return value === null || value === undefined || value === "" ? "—" : String(value);
}

/** Up to two letters for an avatar: the first letters of the first two words, else the first two characters. */
export function initials(source: string): string {
  const trimmed = source.trim();
  const [first, second] = trimmed.split(/\s+/).filter(Boolean);
  if (first && second) return `${first[0]}${second[0]}`.toUpperCase();
  return trimmed.slice(0, 2).toUpperCase();
}
